import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [status, setStatus] = useState("polling");
  const [detail, setDetail] = useState(null);
  const { clear } = useCart();
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          clearInterval(timer);
          setStatus("paid");
          clear();
        } else if (
          data.payment_status === "failed" ||
          data.payment_status === "expired"
        ) {
          clearInterval(timer);
          setStatus("failed");
        } else if (attempts >= maxAttempts) {
          clearInterval(timer);
          setStatus("timeout");
        }
      } catch (e) {
        if (attempts >= maxAttempts) {
          clearInterval(timer);
          setStatus("error");
        }
      }
    }, 2000);

    return () => clearInterval(timer);
     
  }, []);

  return (
    <div className="min-h-screen bg-[var(--mm-bg)] flex items-center justify-center px-6 grain">
      <div className="max-w-lg w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-[var(--mm-accent)] animate-spin mb-6" />
            <h1 className="font-display text-4xl md:text-5xl italic text-[var(--mm-cream)] mb-4">
              Confirming your order...
            </h1>
            <p className="text-[var(--mm-cream-muted)] font-light">
              Hold tight — mama is checking the pot.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[var(--mm-accent)] mb-6" />
            <div className="section-label mb-4">Order Confirmed</div>
            <h1 className="font-display text-4xl md:text-6xl italic text-[var(--mm-cream)] mb-4">
              Thank you<span className="text-[var(--mm-accent)]">.</span>
            </h1>
            <p className="text-[var(--mm-cream-muted)] font-light mb-8 leading-relaxed">
              We've received your order{detail?.customer_name ? `, ${detail.customer_name}` : ""}. A confirmation has been sent to your email. Mama will have it ready soon.
            </p>
            {detail?.amount && (
              <div className="font-display text-2xl text-[var(--mm-cream)] mb-8">
                Total paid: <span className="text-[var(--mm-accent)]">${(detail.amount / 100).toFixed(2)} {(detail.currency || "aud").toUpperCase()}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-3 justify-center" data-testid="success-actions">
              {sessionId && (
                <a href={`/order/${sessionId}`} className="btn-primary" data-testid="track-order-btn">
                  Track your order
                </a>
              )}
              <a href="/" className="btn-outline">Back to home</a>
            </div>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <XCircle size={48} className="mx-auto text-[var(--mm-terracotta)] mb-6" />
            <h1 className="font-display text-4xl italic text-[var(--mm-cream)] mb-4">
              Something went wrong
            </h1>
            <p className="text-[var(--mm-cream-muted)] font-light mb-8">
              We couldn't confirm your payment. If you were charged, get in touch and we'll sort it.
            </p>
            <div className="flex gap-3 justify-center">
              <a href="/" className="btn-outline">Back to Menu</a>
              <a href="mailto:Mamaminasoulfood@gmail.com" className="btn-primary">Contact Us</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
